/* eslint-disable react/no-array-index-key */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-shadow */
/* eslint-disable camelcase */
import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import getCurrencySymbol from 'currency-symbol-map';
import { cloneDeep, isEqual } from 'lodash';
import PropTypes from 'prop-types';

import { transformProducts } from '../../../builder/components/modals/inventory/inventory.util';
import { inventoryControls } from '../../../builder/slice-actions/element';
import { assignFormDesignToCSSVars } from '../fields-customise.style';
import { InventoryWrapper, Wrapper, Total } from './inventory.styled';
import { FormContext } from '../../../shared/contexts/form-context';
import { appendSelectionProps, getAllCtagories } from './helper';
import { setUserInputOnCtrl } from '../../../form/reducer/form';
import { ctrlDefaults } from '../../../../shared/form-lib';
import { selectCtrlIdLabelMap } from '../../selectors';
import { ControlPropType } from '../../ctrl_proptype';
import Categories from './components/categories';
import Product from './components/product';
import {
  ValidationWarning,
  MainfieldWrapper,
  SubtextWrapper,
  PaymentNotice,
  FieldWrapper,
  LabelWrapper,
  Field,
} from '../../common-field-styles';

const { toBackEndShape } = transformProducts;

const InventoryField = ({ ctrl, formDesign }) => {
  const value = ctrl.value ?? { transaction_id: '', products: null };
  const dispatch = useDispatch();

  const [products, setProducts] = useState(appendSelectionProps(ctrl.props.inventory_products, value));
  const [ctrlValidationError, setCtrlValidationError] = useState(ctrl._validationError ?? '');
  const [activeCategoryProducts, setActiveCategoryProducts] = useState(products);
  const { inPreviewPage } = useSelector((state) => state.builder.present);
  const { inBuilder, currentPageNo, inForm } = useContext(FormContext);
  const { inventory_payment } = dispatch(inventoryControls(inForm));
  const shouldShowTotal = products.some(({ price }) => price);
  const [activeCategory, setActiveCategory] = useState('all');
  const ctrlIdLabelMap = useSelector(selectCtrlIdLabelMap);
  const { payments } = useSelector((state) => state.form);
  const fieldLabel = ctrlIdLabelMap?.[ctrl.internal_id];
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);

  const getTotal = (items) => {
    return parseFloat(
      items.reduce((acc, { selection: { quantity }, price }) => acc + Number(quantity) * price, 0)
    ).toFixed(2);
  };

  const totalAmount = `${getCurrencySymbol(ctrl.props.currency || 'USD')}${inForm && total ? total : '0.00'}`;

  const getCategories = async () => setCategories(await getAllCtagories(ctrl.props.inventory_products, inForm));

  const saveUpdatedCtrl = (newValue, isValid) => {
    dispatch(
      setUserInputOnCtrl({
        ctrlId: ctrl.internal_id,
        page: currentPageNo - 1,
        property: 'value',
        newValue,
        isValid,
      })
    );
  };

  const updateProductSelection = ({ id, selection, combination }) => {
    if (!inForm) return;

    let valueProducts = value.products ? cloneDeep(value.products) : [];
    const newProducts = cloneDeep(products);

    const selectedProduct = newProducts.find((product) => product.id === id);
    selectedProduct.selection = selection;

    if (selection.quantity) {
      // Append clean/non-empty option combination to value product
      const valueProduct = { ...selectedProduct, selection: { ...selection, combination } };

      const indexInValueProducts = valueProducts.findIndex((product) => product.id === id);
      if (indexInValueProducts >= 0) valueProducts[indexInValueProducts] = valueProduct;
      else valueProducts.push(valueProduct);
    } else valueProducts = valueProducts.filter((product) => product.id !== id);

    const newValue = valueProducts.length
      ? {
          products: toBackEndShape(valueProducts),
          transaction_id: value.transaction_id,
        }
      : null;

    setTotal(getTotal(valueProducts));
    saveUpdatedCtrl(newValue, true);
    setProducts(newProducts);
  };

  useEffect(() => {
    if (inBuilder) {
      const currentCategories = ctrl.props.inventory_products.flatMap(({ category }) => category);
      const prevCategories = products.flatMap(({ category }) => category);
      if (!isEqual(prevCategories, currentCategories)) getCategories();
      setProducts(appendSelectionProps(ctrl.props.inventory_products));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctrl.props.inventory_products]);

  useEffect(() => {
    let isMounted = true;
    if (isMounted) getCategories();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let newProducts = [...products];
    if (activeCategory !== 'all') {
      newProducts = products.filter((product) => product.category.includes(activeCategory));
    }
    setActiveCategoryProducts(newProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, products]);

  useEffect(() => {
    if (ctrl._validationError) setCtrlValidationError(ctrl._validationError);
  }, [ctrl._validationError]);

  // When product selection is triggered and inventory field value is updates,
  // update inventory payment control necessities(amount and required props).
  useEffect(() => {
    if (inForm && inventory_payment) {
      const { internal_id } = inventory_payment;

      dispatch(
        setUserInputOnCtrl({
          newValue: !!Number(total),
          page: currentPageNo - 1,
          property: 'required',
          ctrlId: internal_id,
          isValid: true,
        })
      );

      dispatch(
        setUserInputOnCtrl({
          page: currentPageNo - 1,
          newValue: Number(total),
          ctrlId: internal_id,
          property: 'amount',
          isValid: true,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  // When inventory payment control is updated from payment attempts,
  // update inventory field value with corresponding payment status.
  useEffect(() => {
    if (inForm && inventory_payment) {
      const { internal_id, type } = inventory_payment;
      const paymentOption = payments[type][internal_id];

      if (paymentOption?.isPaid) {
        const updatedValue = {
          transaction_id: paymentOption.transactionId,
          products: value.products,
        };

        dispatch(
          setUserInputOnCtrl({
            ctrlId: ctrl.internal_id,
            page: currentPageNo - 1,
            newValue: updatedValue,
            property: 'value',
            isValid: true,
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments]);

  const shouldShowPaymentNotice = () => {
    const {
      value,
      props: { required, accept_inventory_payment },
    } = ctrl;
    const inBuilderPreviewAndIsRequired = inPreviewPage && required;
    const inBuilderAndNotInPreview = inBuilder && !inPreviewPage;

    return accept_inventory_payment && (inBuilderAndNotInPreview || inBuilderPreviewAndIsRequired || required || value);
  };

  return (
    <MainfieldWrapper style={assignFormDesignToCSSVars(inBuilder, formDesign)}>
      <FieldWrapper>
        {ctrlValidationError && typeof ctrlValidationError === 'string' && (
          <ValidationWarning className="ui bottom pointing basic label red">{ctrlValidationError}</ValidationWarning>
        )}

        <Field>
          <LabelWrapper required={ctrl.props.required} notInBuilder={!inBuilder}>
            <label
              htmlFor={`fpl-${ctrl.internal_id}`}
              dangerouslySetInnerHTML={{ __html: fieldLabel || ctrlDefaults()[ctrl.type] }}
            />
          </LabelWrapper>

          <Wrapper>
            {categories.length > 1 && (
              <Categories
                updateCategory={(selectedCategory) => setActiveCategory(selectedCategory)}
                activeCategory={activeCategory}
                categories={categories}
              />
            )}

            <InventoryWrapper className={`${ctrl.props.view === 'list' ? 'list' : ''}`} id={`fpl-${ctrl.internal_id}`}>
              {activeCategoryProducts.map((product, index) => (
                <Product
                  formWidth={formDesign?.design?.custom_form_width || ''}
                  ctrlValidationError={ctrlValidationError[product.id]}
                  updateProductSelection={updateProductSelection}
                  outOfStock={ctrl.props.out_of_stock}
                  key={`${product.id}-${index}`}
                  view={ctrl.props.view}
                  product={product}
                />
              ))}
            </InventoryWrapper>

            {shouldShowPaymentNotice() && (
              <PaymentNotice>
                <span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M8 7C8.13261 7 8.25979 7.05268 8.35355 7.14645C8.44732 7.24021 8.5 7.36739 8.5 7.5V10.5C8.5 10.6326 8.44732 10.7598 8.35355 10.8536C8.25979 10.9473 8.13261 11 8 11C7.86739 11 7.74021 10.9473 7.64645 10.8536C7.55268 10.7598 7.5 10.6326 7.5 10.5V7.5C7.5 7.36739 7.55268 7.24021 7.64645 7.14645C7.74021 7.05268 7.86739 7 8 7ZM8 6.25C8.09849 6.25 8.19602 6.2306 8.28701 6.19291C8.37801 6.15522 8.46069 6.09997 8.53033 6.03033C8.59997 5.96069 8.65522 5.87801 8.69291 5.78701C8.7306 5.69602 8.75 5.59849 8.75 5.5C8.75 5.40151 8.7306 5.30398 8.69291 5.21299C8.65522 5.12199 8.59997 5.03931 8.53033 4.96967C8.46069 4.90003 8.37801 4.84478 8.28701 4.80709C8.19602 4.7694 8.09849 4.75 8 4.75C7.80135 4.75 7.61084 4.82891 7.47038 4.96938C7.32991 5.10984 7.251 5.30035 7.251 5.499C7.251 5.69765 7.32991 5.88816 7.47038 6.02862C7.61084 6.16909 7.80135 6.248 8 6.248V6.25ZM2 8C2 7.21207 2.15519 6.43185 2.45672 5.7039C2.75825 4.97595 3.20021 4.31451 3.75736 3.75736C4.31451 3.20021 4.97595 2.75825 5.7039 2.45672C6.43185 2.15519 7.21207 2 8 2C8.78793 2 9.56815 2.15519 10.2961 2.45672C11.0241 2.75825 11.6855 3.20021 12.2426 3.75736C12.7998 4.31451 13.2417 4.97595 13.5433 5.7039C13.8448 6.43185 14 7.21207 14 8C14 9.5913 13.3679 11.1174 12.2426 12.2426C11.1174 13.3679 9.5913 14 8 14C6.4087 14 4.88258 13.3679 3.75736 12.2426C2.63214 11.1174 2 9.5913 2 8ZM8 3C6.67392 3 5.40215 3.52678 4.46447 4.46447C3.52678 5.40215 3 6.67392 3 8C3 9.32608 3.52678 10.5979 4.46447 11.5355C5.40215 12.4732 6.67392 13 8 13C9.32608 13 10.5979 12.4732 11.5355 11.5355C12.4732 10.5979 13 9.32608 13 8C13 6.67392 12.4732 5.40215 11.5355 4.46447C10.5979 3.52678 9.32608 3 8 3Z"
                      fill="#FC830A"
                    />
                  </svg>
                </span>

                {!inBuilder || inPreviewPage ? (
                  <span>
                    There’s a payment field attached to this field, you will be required to pay after you click the <b>“submit”</b> button.
                  </span>
                ) : (
                  <span>
                    This field has now been linked to a payment option. Respondents will be required to complete a payment after clicking the <b>“submit”</b> button. You do not need to add another payment field for this purpose.
                  </span>
                )}
              </PaymentNotice>
            )}

            {shouldShowTotal && (
              <Total>
                <div className="total">Total:</div>
                <div className="sum">
                  <span>{totalAmount}</span>
                </div>
              </Total>
            )}
          </Wrapper>

          <SubtextWrapper id="descriptive text" notInBuilder={!inBuilder}>
            {ctrl.props.subtext}
          </SubtextWrapper>
        </Field>
      </FieldWrapper>
    </MainfieldWrapper>
  );
};

InventoryField.defaultProps = {
  formDesign: null,
};

InventoryField.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  formDesign: PropTypes.shape({
    design: PropTypes.object.isRequired,
    old: PropTypes.object.isRequired,
  }),
};

export default InventoryField;
